/**
 * lib/experiences/categoryMeta.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Category and time-of-day metadata for the Experiences feature.
 *
 * Exports:
 *   CategoryMeta       — type for one category's icon/label/color
 *   CATEGORY_META      — record keyed by category string
 *   TimeMeta           — type for time-of-day icon/label
 *   TIME_META          — record keyed by time_of_day string
 *   PlaceholderEntry   — type for image placeholder gradient
 *   PLACEHOLDER_PALETTE — record keyed by category string
 *
 * Used by:
 *   routes/experiences.tsx — ExperienceRow chips, ImagePlaceholder
 */

// ─── Category metadata ────────────────────────────────────────────────────────

export interface CategoryMeta {
  icon:  string;
  label: string;
  /** oklch color string — used for category chip text */
  color: string;
}

export const CATEGORY_META: Readonly<Record<string, CategoryMeta>> = {
  food:         { icon: "🍜", label: "Gastronomía",   color: "oklch(0.68 0.11 55)"  },
  anime:        { icon: "⛩️",  label: "Anime",         color: "oklch(0.55 0.14 285)" },
  nightlife:    { icon: "🌃", label: "Vida nocturna", color: "oklch(0.50 0.14 255)" },
  culture:      { icon: "🏛️",  label: "Cultura",       color: "oklch(0.45 0.08 235)" },
  art:          { icon: "🎨", label: "Arte",           color: "oklch(0.56 0.14 340)" },
  nature:       { icon: "🌿", label: "Naturaleza",    color: "oklch(0.48 0.12 145)" },
  music:        { icon: "🎵", label: "Música",         color: "oklch(0.56 0.12 30)"  },
  architecture: { icon: "🏗️",  label: "Arquitectura",  color: "oklch(0.47 0.07 210)" },
  wellness:     { icon: "✨", label: "Bienestar",     color: "oklch(0.54 0.09 155)" },
  shopping:     { icon: "🛍️",  label: "Compras",        color: "oklch(0.60 0.10 75)"  },
};

// ─── Time-of-day metadata ─────────────────────────────────────────────────────

export interface TimeMeta {
  icon:  string;
  label: string;
}

export const TIME_META: Readonly<Record<string, TimeMeta>> = {
  morning:   { icon: "🌅", label: "Mañana"    },
  afternoon: { icon: "☀️",  label: "Tarde"     },
  evening:   { icon: "🌇", label: "Atardecer" },
  night:     { icon: "🌙", label: "Noche"     },
  any:       { icon: "📍", label: "Flexible"  },
};

// ─── Activity intensity metadata (Marketplace Comparison V1) ────────────────
// `ExperienceItem.energy` is a real backend field ("high" | "medium" | "low",
// confirmed against engine/destination_intelligence/destination_profile.py
// and engine/archetypes/archetype_rules.py) — an activity-intensity/pacing
// signal, not fabricated. No display label existed anywhere in the frontend
// before this sprint (only an unrelated, unexported color map keyed by the
// same values for itinerary day chips) — added here as the "intensidad"
// attribute in the comparison table. Category remains the honest source for
// the activity's primary focus.

export const ENERGY_META: Readonly<Record<string, string>> = {
  high:   "Alta energía",
  medium: "Energía media",
  low:    "Energía baja",
};

// ─── Image placeholder palettes ───────────────────────────────────────────────
// Per-category emoji + gradient for when image_url is null.
// Future: image_url from Viator/GetYourGuide CDN will replace these.

export interface PlaceholderEntry {
  emoji: string;
  /** CSS oklch color — gradient start */
  from:  string;
  /** CSS oklch color — gradient end */
  to:    string;
}

export const PLACEHOLDER_PALETTE: Readonly<Record<string, PlaceholderEntry>> = {
  food:         { emoji: "🍜", from: "oklch(0.987 0.022 72)",  to: "oklch(0.978 0.018 60)"  },
  nightlife:    { emoji: "🌃", from: "oklch(0.972 0.018 262)", to: "oklch(0.962 0.026 275)" },
  culture:      { emoji: "🏛️",  from: "oklch(0.978 0.014 232)", to: "oklch(0.970 0.012 222)" },
  art:          { emoji: "🎨", from: "oklch(0.978 0.018 342)", to: "oklch(0.970 0.016 328)" },
  nature:       { emoji: "🌿", from: "oklch(0.982 0.020 150)", to: "oklch(0.974 0.016 138)" },
  anime:        { emoji: "⛩️",  from: "oklch(0.976 0.018 292)", to: "oklch(0.966 0.016 280)" },
  music:        { emoji: "🎵", from: "oklch(0.986 0.020 38)",  to: "oklch(0.978 0.016 28)"  },
  architecture: { emoji: "🏗️",  from: "oklch(0.978 0.014 218)", to: "oklch(0.970 0.012 206)" },
  wellness:     { emoji: "✨", from: "oklch(0.984 0.014 162)", to: "oklch(0.976 0.012 150)" },
  shopping:     { emoji: "🛍️",  from: "oklch(0.986 0.016 76)",  to: "oklch(0.978 0.014 65)"  },
};
